export interface LutData {
  title: string | null,
  size: number,
  format: 'rgba8',
  // Limited inRange means that the RGB samples should be scaled to the
  // [(16 / 255), (235 / 255)] range before being looked up in the Matrix.
  inRange: 'full' | 'limited',
  // Limited outRange means that the RGB samples returned from the LUT lookup
  // must be scaled up from [(16 / 255), (235 / 255)] range to [0.0, 1.0]
  // before getting passed on the rest of the pipeline
  outRange: 'full' | 'limited',
  content: Uint8Array,
}

export function parseLutCube(bytes: Uint8Array): LutData {
  const content = new TextDecoder().decode(bytes);
  const lines = content.split('\n');
  const values: number[] = [];

  let size: number | null = null;
  let title: string | null = null;
  let inRange: ('full' | 'limited') = 'full';
  let outRange: ('full' | 'limited') = 'full';

  for (const untrimmedLine of lines) {
    const line = untrimmedLine.trim();

    if (shouldSkipLine(line)) {
      continue;
    }

    if (line.startsWith('TITLE')) {
      title = extractTitle(line);
    } else if (line.startsWith('LUT_3D_SIZE')) {
      size = extractSize(line);
    } else if (line.startsWith('LUT_IN_VIDEO_RANGE')) {
      inRange = 'limited';
    } else if (line.startsWith('LUT_OUT_VIDEO_RANGE')) {
      outRange = 'limited';
    } else {
      values.push(...parseRgbLine(line));
    }
  }

  const validated = validateLutData(size, values);

  return {
    size: validated.size,
    title,
    format: 'rgba8',
    inRange,
    outRange,
    content: new Uint8Array(validated.values),
  };
}

function shouldSkipLine(line: string): boolean {
  return (line === '' || line.startsWith('#'));
}

function extractTitle(line: string): string {
  const secondPart = line.slice('TITLE'.length).trim();
  if (!secondPart.startsWith('"') || !secondPart.endsWith('"')) {
    throw new Error(`Invalid TITLE line: ${line}`);
  }
  return secondPart.slice(1, -1);
}

function extractSize(line: string): number {
  const [, second] = line.split(/\s+/);
  if (second === undefined) {
    throw new Error(`Invalid LUT_3D_SIZE line: ${line}`);
  }

  const value = parseInt(second, 10);
  if (!Number.isFinite(value) || value.toString() !== second) {
    throw new Error(`Invalid LUT_3D_SIZE line: ${line}`);
  }
  return value;
}

function parseRgbLine(line: string): number[] {
  const parts = line.split(/\s+/).map((s) => parseFloat(s));
  const [r, g, b] = parts;

  if (
    (r === undefined) || !Number.isFinite(r) ||
    (g === undefined) || !Number.isFinite(g) ||
    (b === undefined) || !Number.isFinite(b)
  ) {
    throw new Error(`Invalid 3d LUT file line: ${line}`);
  }

  // TODO: Right now this truncates values to 8-bit values. We probably want
  // to store it as 16-bit values when converting to HDR.
  return [
    Math.round(r * 255.0),
    Math.round(g * 255.0),
    Math.round(b * 255.0),
    255, // Alpha (unused)
  ];
}

function validateLutData(size: number | null, values: number[]): { size: number, values: number[] } {
  if (size === null) {
    throw new Error('Missing LUT_3D_SIZE line');
  }
  if (values.length !== size * size * size * 4) {
    throw new Error('Incorrect number of values in LUT file');
  }
  return { size, values };
}
